import { useEffect, useState } from "react";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X } from "lucide-react";

export default function Approvals() {
  const { can } = useAuth();
  const { outletName } = useMeta();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [target, setTarget] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/approvals", { params: { status: status === "all" ? undefined : status } }); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const decide = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/approvals/${target.id}/decide`, { decision, reason }); toast.success(`Request ${decision}`); setTarget(null); setReason(""); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const open = (r, d) => { setTarget(r); setDecision(d); setReason(""); };

  const columns = [
    { key: "entity_type", label: "Type", render: (r) => <span className="capitalize">{String(r.entity_type).replace(/_/g, " ")}</span> },
    { key: "label", label: "Reference", render: (r) => <span className="font-medium">{r.label || "—"}</span> },
    { key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) },
    { key: "amount", label: "Amount", className: "text-right", cellClassName: "tabular text-right font-semibold", render: (r) => money(r.amount) },
    { key: "requested_by_name", label: "Requested By" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", label: "", render: (r) => r.status === "pending" && can("approvals:approve") ? (
      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => open(r, "approved")} data-testid={`approve-${r.id}`}><Check className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => open(r, "rejected")} data-testid={`reject-${r.id}`}><X className="h-3.5 w-3.5" /></Button>
      </div>
    ) : <span className="text-xs text-muted-foreground">{r.approver_name || "—"}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Approvals" subtitle="Review and decide on purchases, expenses and budget changes." />
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>
      <DataTable columns={columns} rows={rows} loading={loading} total={rows.length} emptyTitle="Nothing here" emptyHint="No approval requests in this state." />

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent data-testid="decide-form">
          <DialogHeader><DialogTitle className="font-display capitalize">{decision === "approved" ? "Approve" : "Reject"} request</DialogTitle></DialogHeader>
          <form onSubmit={decide} className="space-y-4">
            <div className="rounded-sm border border-border bg-secondary/50 p-3 text-sm">
              {target?.label} · <b className="tabular">{money(target?.amount)}</b>
            </div>
            <div className="space-y-1.5"><Label className="eyebrow">Reason / remarks</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" data-testid="decide-reason" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
              <Button type="submit" disabled={busy} className={decision === "rejected" ? "bg-destructive hover:bg-destructive/90" : ""} data-testid="decide-submit">{busy ? "Saving…" : decision === "approved" ? "Approve" : "Reject"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
